import React, { useState, useEffect, useRef } from 'react';
import { Link, useNavigate, useLocation } from 'react-router-dom';
import axios from 'axios';
import { useAuth } from '../contexts/AuthContext';
import { Button } from '../components/ui/button';
import { Badge } from '../components/ui/badge';
import {
  Calendar, Video, AlertTriangle,
  MapPin, FileText, Heart, ArrowRight, Sparkles,
  Compass, MessageSquare,
  Zap, Mic, Loader2,
} from 'lucide-react';
import { toast } from 'sonner';
import { DashboardShell } from '../components/brand/DashboardShell';
import { MorningTodayWidget } from '../components/brand/MorningTodayWidget';
import { patientNavItems } from '../components/brand/navItems';
import { BentoGrid, BentoTile, Counter, RingScore, LiveDot, PhotoEmptyState, TileHeader } from '../components/brand/BentoWidgets';
import { ProfilePhotoUpload } from '../components/brand/ProfilePhotoUpload';
import { CareJourneyTracker } from '../components/brand/CareJourneyTracker';
import { PatientInsuranceCard } from '../components/brand/InsuranceCards';
import { ShareVisitButton } from '../components/brand/ShareVisitButton';
import PostVisitReviewPrompt from '../components/brand/PostVisitReviewPrompt';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
const EMPTY_IMG = 'https://static.prod-images.emergentagent.com/jobs/d66875ad-9bb9-40cd-9a51-63fb813a049d/images/425acea9f51be82774585541d81ccbfbcd5a240ae13644bde8dd1653a6c1057d.png';
const CHECKIN_BG = 'https://static.prod-images.emergentagent.com/jobs/d66875ad-9bb9-40cd-9a51-63fb813a049d/images/d9a03be545bf79920de0d9644a40086e6b82414d4161e605e439d930508b01d8.png';

const MOODS = [
  { key: 'great', emoji: '◎', label: 'Great' },
  { key: 'okay', emoji: '◐', label: 'Okay' },
  { key: 'tired', emoji: '◌', label: 'Tired' },
  { key: 'pain', emoji: '◍', label: 'Pain' },
];

export default function PatientDashboard() {
  const { user, getAuthHeader, token } = useAuth();
  const navigate = useNavigate();
  const location = useLocation();

  const [profile, setProfile] = useState(null);
  const [matches, setMatches] = useState([]);
  const [appointments, setAppointments] = useState([]);
  const [savedClinicians, setSavedClinicians] = useState([]);
  const [loading, setLoading] = useState(true);
  const [mood, setMood] = useState(null);
  const [rapidMatches, setRapidMatches] = useState([]);
  const [searchingRapid, setSearchingRapid] = useState(false);
  const [recording, setRecording] = useState(false);
  const [voiceBusy, setVoiceBusy] = useState(false);
  const mediaRecorderRef = useRef(null);

  useEffect(() => { fetchData(); }, []);

  const onRapidNeed = async () => {
    setSearchingRapid(true);
    try {
      const resp = await axios.post(`${API}/patient/rapid-match`, {}, getAuthHeader());
      setRapidMatches(resp.data || []);
      if ((resp.data || []).length > 0) {
        toast.success(`Found ${resp.data.length} clinician${resp.data.length > 1 ? 's' : ''} nearby`);
      } else {
        toast.info('No clinicians available within ~50 miles right now.');
      }
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Could not complete rapid match. Check your profile location.');
    } finally {
      setSearchingRapid(false);
    }
  };

  const startVoiceSearch = async () => {
    if (recording) { mediaRecorderRef.current?.stop(); return; }
    try {
      const stream = await navigator.mediaDevices.getUserMedia({ audio: true });
      const chunks = [];
      const mr = new MediaRecorder(stream);
      mediaRecorderRef.current = mr;
      mr.ondataavailable = (e) => { if (e.data.size > 0) chunks.push(e.data); };
      mr.onstop = async () => {
        stream.getTracks().forEach((t) => t.stop());
        setRecording(false);
        const blob = new Blob(chunks, { type: 'audio/webm' });
        if (blob.size === 0) return;
        setVoiceBusy(true);
        try {
          const b64 = await new Promise((res, rej) => {
            const fr = new FileReader();
            fr.onloadend = () => res(String(fr.result).split(',')[1]);
            fr.onerror = rej;
            fr.readAsDataURL(blob);
          });
          const resp = await axios.post(`${API}/patient/voice-search`, { audio_data: b64, audio_format: 'webm' }, getAuthHeader());
          const { transcript, filters } = resp.data || {};
          if (transcript) toast.success(`Heard: "${transcript.slice(0, 80)}"`);
          const kw = filters?.keywords || filters?.primary_diagnosis || (filters?.care_needs || []).join(' ') || transcript || '';
          const params = new URLSearchParams();
          if (kw) params.set('q', kw);
          if (filters?.preferred_visit_type && filters.preferred_visit_type !== 'both') params.set('visit_type', filters.preferred_visit_type);
          navigate(`/patient/find-clinicians?${params.toString()}`);
        } catch (err) {
          toast.error('Could not understand the recording. Please try again.');
        } finally {
          setVoiceBusy(false);
        }
      };
      mr.start();
      setRecording(true);
      toast.info('Listening… tap again to stop.');
    } catch (e) {
      toast.error('Microphone access is required for voice search.');
    }
  };

  const fetchData = async () => {
    try {
      const [profileRes, matchesRes, appointmentsRes, savedRes] = await Promise.all([
        axios.get(`${API}/patient/profile`, getAuthHeader()),
        axios.get(`${API}/patient/matches`, getAuthHeader()),
        axios.get(`${API}/appointments`, getAuthHeader()),
        axios.get(`${API}/patient/saved`, getAuthHeader()).catch(() => ({ data: { clinicians: [] } })),
      ]);
      setProfile(profileRes.data);
      setMatches(matchesRes.data);
      setAppointments(appointmentsRes.data);
      setSavedClinicians(savedRes.data.clinicians || []);
    } catch (error) {
      console.error('Error fetching data:', error);
    } finally {
      setLoading(false);
    }
  };

  const removeSaved = async (clinicianId) => {
    setSavedClinicians((prev) => prev.filter((c) => c.id !== clinicianId));
    try {
      await axios.post(`${API}/patient/saved/${clinicianId}`, {}, getAuthHeader());
    } catch (e) { /* best-effort; will resync on next load */ }
  };

  const findMatches = async () => {
    try {
      toast.info('Finding your best matches...');
      const response = await axios.post(`${API}/patient/find-matches`, {}, getAuthHeader());
      setMatches(response.data);
      toast.success(`Found ${response.data.length} matches!`);
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Error finding matches');
    }
  };

  const upcoming = appointments
    .filter(a => a.status === 'scheduled' || a.status === 'confirmed')
    .slice(0, 4);

  const navItems = patientNavItems({ pathname: location.pathname });

  if (loading) {
    return (
      <div className="min-h-screen bg-cn-bg flex items-center justify-center">
        <div className="w-8 h-8 border-4 border-cn-primary border-t-transparent rounded-full animate-spin" />
      </div>
    );
  }

  const careScore = profile?.care_complexity_score || 0;
  const apptList = appointments || [];
  const journeySteps = [
    { key: 'intake', label: 'Intake', hint: 'A guided chat with your AI Care Navigator', cta: 'Start the conversation', done: !!profile?.ai_assessment_complete },
    { key: 'matching', label: 'Matching', hint: 'Meet clinicians matched to your needs', cta: 'See your matches', done: matches.length > 0 },
    { key: 'booking', label: 'Booking', hint: 'Schedule your first visit', cta: 'Book a visit', done: apptList.length > 0 },
    { key: 'active', label: 'Active care', hint: 'Ongoing, supported care', cta: 'View your visits', done: apptList.some((a) => ['confirmed', 'completed', 'in_progress'].includes(a.status)) },
  ];

  const onJourneyStep = (s) => {
    if (s.key === 'intake') navigate('/patient/assessment');
    else if (s.key === 'matching') {
      if (profile?.ai_assessment_complete) findMatches();
      else navigate('/patient/assessment');
    } else if (s.key === 'booking') navigate('/patient/find-clinicians');
    else document.querySelector('[data-testid="patient-dashboard"]')?.scrollIntoView({ behavior: 'smooth' });
  };

  return (
    <DashboardShell
      navItems={navItems}
      title={`Hi ${user?.first_name || 'there'}`}
      subtitle="Here's your care landscape today — gentle, organized, and made for you."
      rightSlot={
        <Link to="/patient/navigator">
          <Button className="cn-btn cn-btn-primary !py-2.5" data-testid="care-navigator-shortcut">
            <Sparkles className="w-4 h-4" /> Care Navigator
          </Button>
        </Link>
      }
      testId="patient-dashboard"
    >
      <div className="mb-5">
        <MorningTodayWidget getAuthHeader={getAuthHeader} />
      </div>

      {/* Path back to the calm Today view for users who clicked into the
          full power-user dashboard but want the simpler default again. */}
      <div className="mb-5 flex justify-end">
        <Link
          to="/patient/dashboard"
          className="text-sm font-semibold text-cn-text-muted hover:text-cn-primary transition-colors"
          data-testid="full-dashboard-back-to-today"
        >
          &larr; Back to Today
        </Link>
      </div>

      {/* CaraLoom 2.0 Care suite: hero adapts to whether the family already has
          active care. New families see "Find care now"; active families see
          "Today's care" with their circle + next visit — continuity, not
          marketplace search. Keeps the V1 hero behavior for first-time users. */}
      {(() => {
        const upcoming = (appointments || [])
          .filter((a) => a.status !== 'cancelled')
          .sort((a, b) => new Date(a.scheduled_at || a.start_time || 0) - new Date(b.scheduled_at || b.start_time || 0));
        const nextVisit = upcoming[0];
        const activeCircle = (matches || []).filter((m) => m.status !== 'declined' && m.status !== 'cancelled').length;
        const hasActiveCare = activeCircle > 0 || upcoming.length > 0;
        if (hasActiveCare) {
          const visitWhen = nextVisit
            ? new Date(nextVisit.scheduled_at || nextVisit.start_time || nextVisit.created_at).toLocaleString(undefined, { weekday: 'short', month: 'short', day: 'numeric', hour: 'numeric', minute: '2-digit' })
            : null;
          return (
            <Link to="/patient/care-plan" className="block mb-6 group" data-testid="todays-care-hero">
              <div className="cn-card p-5 sm:p-6 flex flex-col sm:flex-row sm:items-center gap-4 border-cn-primary/30 bg-gradient-to-br from-cn-primary/8 via-cn-primary/5 to-transparent transition-colors group-hover:border-cn-primary/50">
                <div className="w-14 h-14 rounded-2xl bg-cn-primary/15 flex items-center justify-center text-cn-primary shrink-0">
                  <Heart className="w-6 h-6" />
                </div>
                <div className="flex-1 min-w-0">
                  <p className="font-display text-xl font-bold text-cn-text dark:text-white">
                    Today&apos;s care
                  </p>
                  <p className="text-sm text-cn-text-muted mt-0.5">
                    {activeCircle > 0 ? `${activeCircle} clinician${activeCircle === 1 ? '' : 's'} in your care circle` : 'No active matches'}
                    {visitWhen ? ` · Next visit: ${visitWhen}` : ''}
                  </p>
                </div>
                <span className="cn-btn cn-btn-primary shrink-0" data-testid="todays-care-hero-cta">
                  Open care plan <ArrowRight className="w-4 h-4" />
                </span>
              </div>
            </Link>
          );
        }
        // First-time / inactive families — keep the V1 "find care" hero.
        return (
          <Link to="/patient/find-care" className="block mb-6 group" data-testid="find-care-banner">
            <div className="cn-card p-5 sm:p-6 flex flex-col sm:flex-row sm:items-center gap-4 border-cn-primary/30 bg-gradient-to-br from-cn-primary/8 via-cn-primary/5 to-transparent transition-colors group-hover:border-cn-primary/50">
              <div className="w-14 h-14 rounded-2xl bg-cn-primary/15 flex items-center justify-center text-cn-primary shrink-0">
                <Sparkles className="w-6 h-6" />
              </div>
              <div className="flex-1 min-w-0">
                <p className="font-display text-xl font-bold text-cn-text dark:text-white">
                  Need to find care for someone? Start here.
                </p>
                <p className="text-sm text-cn-text-muted mt-0.5">
                  Three soft questions — what kind of care, when, and where — and we&apos;ll show you the right clinicians.
                </p>
              </div>
              <span className="cn-btn cn-btn-primary shrink-0" data-testid="find-care-banner-cta">
                Find care now <ArrowRight className="w-4 h-4" />
              </span>
            </div>
          </Link>
        );
      })()}

      {/* Visual Care Journey Tracker */}
      <CareJourneyTracker steps={journeySteps} onStepClick={onJourneyStep} />

      {/* Post-visit review prompt (renders only if eligible bookings exist) */}
      <div className="mb-6">
        <PostVisitReviewPrompt token={token} />
      </div>

      {/* Rapid match + Voice search */}
      <div className="grid sm:grid-cols-2 gap-4 mb-6">
        <div className="cn-card p-5 flex items-center gap-4 border-cn-accent/30 bg-cn-accent/5" data-testid="rapid-match-card">
          <div className="w-12 h-12 rounded-2xl bg-cn-accent/15 text-cn-accent flex items-center justify-center shrink-0">
            <Zap className="w-6 h-6" />
          </div>
          <div className="flex-1 min-w-0">
            <h3 className="font-bold text-cn-text dark:text-white">Immediate care need?</h3>
            <p className="text-xs text-cn-text-muted">Find the 3 closest available clinicians instantly.</p>
          </div>
          <Button onClick={onRapidNeed} disabled={searchingRapid} className="cn-btn cn-btn-accent !py-2 shrink-0" data-testid="rapid-match-btn">
            {searchingRapid ? <Loader2 className="w-4 h-4 animate-spin" /> : 'Find now'}
          </Button>
        </div>

        <div className="cn-card p-5 flex items-center gap-4 border-cn-primary/30 bg-cn-primary/5" data-testid="voice-search-card">
          <div className={`w-12 h-12 rounded-2xl flex items-center justify-center shrink-0 ${recording ? 'bg-red-500/15 text-red-500' : 'bg-cn-primary/15 text-cn-primary'}`}>
            <Mic className={`w-6 h-6 ${recording ? 'animate-pulse' : ''}`} />
          </div>
          <div className="flex-1 min-w-0">
            <h3 className="font-bold text-cn-text dark:text-white">Voice search</h3>
            <p className="text-xs text-cn-text-muted">Speak your care needs — AI finds the right match.</p>
          </div>
          <Button onClick={startVoiceSearch} disabled={voiceBusy} variant="outline" className="!py-2 shrink-0 border-cn-primary/30" data-testid="voice-search-btn">
            {voiceBusy ? <Loader2 className="w-4 h-4 animate-spin" /> : (recording ? 'Stop' : 'Speak')}
          </Button>
        </div>
      </div>

      {rapidMatches.length > 0 && (
        <div className="mb-8 cn-card p-6 border-cn-accent/30 bg-cn-accent/5 animate-in fade-in zoom-in duration-300" data-testid="rapid-match-results">
          <TileHeader
            eyebrow="Urgent results"
            title="Closest available clinicians"
            action={<Button variant="ghost" size="sm" onClick={() => setRapidMatches([])} data-testid="rapid-clear-btn">Clear</Button>}
          />
          <div className="mt-4 grid sm:grid-cols-3 gap-4">
            {rapidMatches.map((m, i) => (
              <div key={i} className="p-4 rounded-2xl bg-white dark:bg-cn-ink-800 border border-cn-accent/20 shadow-sm" data-testid={`rapid-match-${i}`}>
                <p className="font-bold text-sm text-cn-text dark:text-white">{m.clinician?.first_name} {m.clinician?.last_name}</p>
                <p className="text-xs text-cn-text-muted">{m.profile?.license_type || 'Clinician'}{m.profile?.city ? ` · ${m.profile.city}` : ''}</p>
                <p className="text-xs text-cn-accent font-semibold mt-1">{m.distance_miles != null ? `${m.distance_miles} mi away` : 'Nearby'}</p>
                <Button className="cn-btn cn-btn-primary w-full mt-3 !py-1.5 text-xs" onClick={() => navigate(`/patient/book/${m.clinician?.id}`)} data-testid={`rapid-book-${i}`}>Book now</Button>
              </div>
            ))}
          </div>
        </div>
      )}

      {/* Conversational onboarding hook */}
      {!profile?.ai_assessment_complete && (
        <div className="mb-6 cn-card p-4 sm:p-5 flex flex-col sm:flex-row sm:items-center gap-4 border-cn-accent/40 bg-cn-accent/5 dark:bg-cn-accent/10" data-testid="assessment-nudge">
          <div className="w-12 h-12 rounded-2xl bg-cn-accent/15 flex items-center justify-center text-cn-accent shrink-0">
            <Sparkles className="w-5 h-5" />
          </div>
          <div className="flex-1">
            <p className="font-bold text-cn-text dark:text-white">Chat with your AI Care Navigator</p>
            <p className="text-sm text-cn-text-muted">A warm 3-minute conversation — no clinical forms — to understand your needs and unlock personalized matches.</p>
          </div>
          <Link to="/patient/assessment">
            <Button className="cn-btn cn-btn-accent" data-testid="start-assessment-btn">
              Start the conversation <ArrowRight className="w-4 h-4" />
            </Button>
          </Link>
        </div>
      )}

      {/* Bento grid */}
      <BentoGrid className="auto-rows-[minmax(120px,auto)]">
        {/* HERO — Today's Care */}
        <BentoTile span="md:col-span-8 lg:col-span-7" rowSpan="lg:row-span-2" className="p-7 sm:p-9 text-white border-transparent" hover={false} testId="today-care-hero"
          style={{ background: 'linear-gradient(135deg, #0F172A 0%, #1E293B 55%, #334155 100%)' }}>
          <div className="absolute -right-16 -top-16 w-72 h-72 rounded-full bg-cn-accent/30 blur-3xl" />
          <div className="absolute -left-10 -bottom-10 w-56 h-56 rounded-full bg-white/10 blur-2xl" />
          <div className="relative h-full flex flex-col">
            <div className="flex items-start justify-between">
              <div>
                <p className="text-xs uppercase tracking-[0.14em] text-white/70 font-bold">Today · {new Date().toLocaleDateString(undefined, { weekday: 'long', month: 'long', day: 'numeric' })}</p>
                <h2 className="cn-display text-3xl sm:text-4xl mt-2 max-w-md leading-tight text-white">
                  Your care plan is breathing steady.
                </h2>
              </div>
              <LiveDot color="#fff" label="Live" />
            </div>

            <div className="mt-6 grid grid-cols-3 gap-4">
              <div className="rounded-2xl bg-white/10 backdrop-blur p-4">
                <p className="text-[11px] uppercase tracking-wider text-white/70 font-semibold">Care Score</p>
                <p className="text-3xl font-display font-extrabold mt-1"><Counter value={careScore} /><span className="text-base text-white/60">/100</span></p>
              </div>
              <div className="rounded-2xl bg-white/10 backdrop-blur p-4">
                <p className="text-[11px] uppercase tracking-wider text-white/70 font-semibold">Care Type</p>
                <p className="text-xl font-display font-bold mt-1 capitalize leading-tight">{(profile?.preferred_care_type || 'Not set').replace('_', ' ')}</p>
                <p className="text-xs text-white/70 mt-1">{profile?.recommended_care_level || 'Pending assessment'}</p>
              </div>
              <div className="rounded-2xl bg-white/10 backdrop-blur p-4">
                <p className="text-[11px] uppercase tracking-wider text-white/70 font-semibold">Urgency</p>
                <p className="text-xl font-display font-bold mt-1">{profile?.urgency_level || 'N/A'}</p>
                <p className="text-xs text-white/70 mt-1">{profile?.urgency_level === 'High Risk' ? 'Care team notified' : 'Stable'}</p>
              </div>
            </div>

            <div className="mt-auto pt-7 flex flex-wrap items-center gap-3">
              <Link to="/patient/find-clinicians">
                <Button className="cn-btn !bg-white !text-cn-primary hover:!bg-cn-bg" data-testid="hero-find-clinicians-btn">
                  Find Clinicians <ArrowRight className="w-4 h-4" />
                </Button>
              </Link>
              <Button onClick={() => window.dispatchEvent(new Event('cara:open'))} className="cn-btn !bg-white/15 hover:!bg-white/25 !text-white border border-white/20" data-testid="hero-ask-cara-btn">
                <Sparkles className="w-4 h-4" /> Ask Cara
              </Button>
            </div>
          </div>
        </BentoTile>

        {/* Wellness ring */}
        <BentoTile span="md:col-span-4 lg:col-span-5" className="p-6 flex flex-col" testId="wellness-tile">
          <TileHeader eyebrow="Wellness ring" title="How you're doing" />
          <div className="flex items-center gap-6 mt-4">
            <RingScore value={careScore} size={108} label="Care" />
            <div className="flex-1 space-y-2.5">
              {[
                { label: 'Adherence', v: 82, c: 'var(--cn-primary)' },
                { label: 'Mobility', v: 64, c: 'var(--cn-secondary)' },
                { label: 'Mood', v: 71, c: 'var(--cn-accent)' },
              ].map((row) => (
                <div key={row.label}>
                  <div className="flex justify-between text-[11px] uppercase tracking-wider text-cn-text-muted font-semibold mb-1">
                    <span>{row.label}</span>
                    <span>{row.v}%</span>
                  </div>
                  <div className="h-2 rounded-full bg-cn-border/60 overflow-hidden">
                    <div className="h-full rounded-full" style={{ width: `${row.v}%`, background: row.c }} />
                  </div>
                </div>
              ))}
            </div>
          </div>
        </BentoTile>

        {/* Profile photo */}
        <BentoTile span="md:col-span-4 lg:col-span-4" className="p-6 flex flex-col" testId="patient-photo-tile">
          <TileHeader eyebrow="Profile" title="Your photo" />
          <div className="mt-4">
            <ProfilePhotoUpload
              currentUrl={profile?.photo_url}
              onUploaded={(url) => setProfile((p) => ({ ...(p || {}), photo_url: url }))}
              label="Profile photo"
              hint="Helps your care team recognize you."
              testId="patient-photo-upload"
            />
          </div>
        </BentoTile>

        {/* Daily Check-in with photo bg */}
        <BentoTile span="md:col-span-4 lg:col-span-4" className="p-0" testId="daily-checkin-tile">
          <div className="relative h-full min-h-[200px]">
            <img src={CHECKIN_BG} alt="" className="absolute inset-0 w-full h-full object-cover" />
            <div className="absolute inset-0 bg-gradient-to-t from-white/95 via-white/60 to-white/30 dark:from-[#0F172A]/95 dark:via-[#0F172A]/60" />
            <div className="relative p-6 h-full flex flex-col">
              <TileHeader eyebrow="Daily check-in" title="How are you feeling?" />
              <div className="mt-auto pt-6 grid grid-cols-4 gap-2">
                {MOODS.map((m) => (
                  <button
                    key={m.key}
                    onClick={() => { setMood(m.key); toast.success(`Logged: ${m.label}`); }}
                    data-testid={`mood-${m.key}`}
                    className={`flex flex-col items-center gap-1 py-3 rounded-2xl border transition-all ${
                      mood === m.key
                        ? 'bg-cn-primary text-white border-cn-primary shadow-md'
                        : 'bg-white/80 dark:bg-white/5 border-cn-border/60 hover:border-cn-primary/40 text-cn-text'
                    }`}
                  >
                    <span className="text-xl">{m.emoji}</span>
                    <span className="text-[11px] font-semibold">{m.label}</span>
                  </button>
                ))}
              </div>
            </div>
          </div>
        </BentoTile>

        {/* Active matches counter */}
        <BentoTile span="md:col-span-4 lg:col-span-4" className="p-6" testId="active-matches-tile">
          <TileHeader eyebrow="Care matches" title="Active matches" action={<LiveDot label="Updated" />} />
          <div className="mt-4 flex items-end gap-4">
            <p className="font-display font-extrabold text-5xl text-cn-text dark:text-white leading-none">
              <Counter value={matches.length} />
            </p>
            <div className="text-xs text-cn-text-muted pb-1">
              <p>clinicians available</p>
              <p className="text-cn-success font-semibold mt-0.5">↗ matched to your needs</p>
            </div>
          </div>
          <Button onClick={findMatches} className="cn-btn cn-btn-ghost mt-3 w-full !py-2" data-testid="find-matches-btn" disabled={!profile?.ai_assessment_complete}>
            Refresh matches
          </Button>
        </BentoTile>

        {/* Appointments counter */}
        <BentoTile span="md:col-span-4 lg:col-span-4" className="p-6" testId="appointments-tile">
          <TileHeader eyebrow="Schedule" title="Appointments" />
          <div className="mt-4 flex items-end gap-4">
            <p className="font-display font-extrabold text-5xl text-cn-text dark:text-white leading-none">
              <Counter value={appointments.length} />
            </p>
            <div className="text-xs text-cn-text-muted pb-1">
              <p>{upcoming.length} upcoming</p>
              <p className="text-cn-secondary font-semibold mt-0.5">{appointments.filter(a => a.appointment_type === 'telehealth').length} telehealth</p>
            </div>
          </div>
          <div className="mt-4 flex gap-1.5">
            {[...Array(7)].map((_, i) => (
              <div key={i} className={`flex-1 rounded-md ${i < upcoming.length ? 'bg-cn-primary' : 'bg-cn-border/60'}`} style={{ height: `${20 + (i * 4)}px` }} />
            ))}
          </div>
        </BentoTile>

        {/* AI Care Navigator promo */}
        <BentoTile span="md:col-span-8 lg:col-span-7" className="p-6 sm:p-7 border-cn-primary/15" testId="ai-navigator-promo" hover={false}>
          <div className="flex items-center gap-5">
            <div className="w-14 h-14 rounded-2xl bg-cn-primary/10 text-cn-primary flex items-center justify-center">
              <Compass className="w-7 h-7" />
            </div>
            <div className="flex-1 min-w-0">
              <p className="cn-eyebrow">Care Navigator</p>
              <h3 className="cn-display text-xl text-cn-text dark:text-white">Not sure what kind of care you need?</h3>
              <p className="text-sm text-cn-text-muted mt-1">A few warm questions and we&apos;ll point you in the right direction.</p>
            </div>
            <Link to="/patient/navigator" className="hidden sm:block">
              <Button className="cn-btn cn-btn-primary" data-testid="care-navigator-btn">Start</Button>
            </Link>
          </div>
        </BentoTile>

        {/* Cara assistant promo */}
        <BentoTile span="md:col-span-4 lg:col-span-5" className="p-6 sm:p-7" testId="cara-promo">
          <TileHeader eyebrow="Meet Cara" title="Your always-on care guide" />
          <p className="text-sm text-cn-text-muted mt-2">Ask questions about your care, appointments, and next steps — anytime.</p>
          <Button onClick={() => window.dispatchEvent(new Event('cara:open'))} className="cn-btn cn-btn-ghost mt-4" data-testid="open-cara-btn">
            <Sparkles className="w-4 h-4" /> Ask Cara
          </Button>
        </BentoTile>

        {/* Matches list */}
        <BentoTile span="col-span-full lg:col-span-7" className="p-6 sm:p-7" id="matches" testId="matches-section">
          <TileHeader
            eyebrow="Your shortlist"
            title="Care matches"
            action={
              <Button onClick={findMatches} disabled={!profile?.ai_assessment_complete} className="cn-btn cn-btn-ghost !py-2 hidden sm:inline-flex" data-testid="find-matches-section-btn">
                Refresh
              </Button>
            }
          />
          {matches.length === 0 ? (
            <div className="mt-5">
              <PhotoEmptyState
                image={EMPTY_IMG}
                title="No matches yet"
                body={profile?.ai_assessment_complete ? 'Tap refresh to discover care matches tailored to you.' : 'Finish your assessment to meet matched clinicians.'}
                action={
                  <Link to={profile?.ai_assessment_complete ? '/patient/find-clinicians' : '/patient/assessment'}>
                    <Button className="cn-btn cn-btn-primary" data-testid="matches-empty-cta">{profile?.ai_assessment_complete ? 'Find Clinicians' : 'Complete Assessment'}</Button>
                  </Link>
                }
                testId="matches-empty"
              />
            </div>
          ) : (
            <div className="mt-5 space-y-3">
              {matches.slice(0, 4).map((match, i) => (
                <div
                  key={match.id}
                  className={`flex flex-col sm:flex-row sm:items-center gap-4 p-4 rounded-2xl border border-cn-border/70 hover:border-cn-primary/40 hover:shadow-md transition-all animate-fade-in-up stagger-${(i % 5) + 1}`}
                  data-testid={`match-card-${i}`}
                >
                  <div className="w-12 h-12 rounded-2xl bg-cn-primary/10 text-cn-primary flex items-center justify-center font-bold overflow-hidden">
                    {match.clinician_profile?.photo_url ? (
                      <img src={match.clinician_profile.photo_url} alt="" className="w-full h-full object-cover" />
                    ) : (
                      <>{match.clinician?.first_name?.[0]}{match.clinician?.last_name?.[0]}</>
                    )}
                  </div>
                  <div className="flex-1 min-w-0">
                    <h4 className="font-semibold text-cn-text dark:text-white truncate">{match.clinician?.first_name} {match.clinician?.last_name}</h4>
                    <p className="text-xs text-cn-text-muted truncate">
                      {match.clinician_profile?.license_type} · {match.clinician_profile?.years_of_experience || 0} yrs exp
                    </p>
                    <div className="flex gap-1 mt-1.5">
                      {match.clinician_profile?.specialties?.slice(0, 2).map((s, j) => (
                        <Badge key={j} variant="secondary" className="text-[10px]">{s}</Badge>
                      ))}
                    </div>
                  </div>
                  <RingScore value={match.match_score} size={64} label="Fit" />
                  <div className="flex gap-2">
                    <Link to={`/patient/book/${match.clinician_id}`}>
                      <Button className="cn-btn cn-btn-primary !py-2" data-testid={`book-btn-${i}`}>
                        <Calendar className="w-4 h-4" /> Book
                      </Button>
                    </Link>
                    <Link to="/patient/messages">
                      <Button className="cn-btn cn-btn-ghost !py-2" data-testid={`message-btn-${i}`}>
                        <MessageSquare className="w-4 h-4" />
                      </Button>
                    </Link>
                  </div>
                </div>
              ))}
            </div>
          )}
        </BentoTile>

        {/* Upcoming appointments rail */}
        <BentoTile span="col-span-full lg:col-span-5" className="p-6 sm:p-7" id="appointments" testId="appointments-section">
          <TileHeader eyebrow="Up next" title="Appointments" />
          {upcoming.length === 0 ? (
            <div className="mt-6 text-center py-10 rounded-2xl bg-cn-bg/60 border border-dashed border-cn-border">
              <Calendar className="w-10 h-10 text-cn-text-muted mx-auto mb-3" />
              <p className="text-sm text-cn-text-muted">No appointments scheduled</p>
            </div>
          ) : (
            <div className="mt-5 space-y-3">
              {upcoming.map((appt, i) => (
                <div key={appt.id} className="flex items-center gap-3 p-3 rounded-2xl border border-cn-border/70 hover:bg-cn-bg/60 transition-colors" data-testid={`appointment-card-${i}`}>
                  <div className={`w-11 h-11 rounded-xl flex items-center justify-center ${appt.appointment_type === 'telehealth' ? 'bg-cn-primary/10 text-cn-primary' : 'bg-cn-secondary/15 text-cn-secondary'}`}>
                    {appt.appointment_type === 'telehealth' ? <Video className="w-5 h-5" /> : <MapPin className="w-5 h-5" />}
                  </div>
                  <div className="flex-1 min-w-0">
                    <p className="font-semibold text-cn-text dark:text-white text-sm truncate">{appt.clinician?.first_name} {appt.clinician?.last_name}</p>
                    <p className="text-xs text-cn-text-muted">{appt.scheduled_date} · {appt.scheduled_time}</p>
                    {appt.status !== 'completed' && (
                      <ShareVisitButton appointmentId={appt.id} testId={`share-visit-${i}`} />
                    )}
                  </div>
                  {appt.appointment_type === 'telehealth' && appt.status !== 'completed' && (
                    <Link to={`/telehealth/${appt.id}`}>
                      <Button className="cn-btn cn-btn-primary !py-1.5 !px-3 !text-xs" data-testid={`join-call-${i}`}>Join</Button>
                    </Link>
                  )}
                </div>
              ))}
            </div>
          )}
        </BentoTile>

        {/* Insurance / coverage */}
        <BentoTile span="col-span-full lg:col-span-5" className="p-6 sm:p-7" id="insurance" testId="patient-insurance-tile">
          <PatientInsuranceCard getAuthHeader={getAuthHeader} />
        </BentoTile>

        {/* Care needs / plan */}
        <BentoTile span="col-span-full" className="p-6 sm:p-7" testId="care-plan-tile">
          <TileHeader eyebrow="Care plan" title="What we know about your needs" action={
            <Link to="/patient/assessment"><Button className="cn-btn cn-btn-ghost !py-2 hidden sm:inline-flex">Update</Button></Link>
          } />
          {profile?.ai_assessment_complete ? (
            <div className="mt-5 grid grid-cols-1 md:grid-cols-3 gap-4">
              <div className="p-4 rounded-2xl bg-cn-primary/8 border border-cn-primary/15">
                <p className="text-[11px] uppercase tracking-wider text-cn-primary font-bold">Recommended care level</p>
                <p className="font-display text-lg font-bold text-cn-text dark:text-white mt-1">{profile.recommended_care_level}</p>
              </div>
              <div className="p-4 rounded-2xl bg-cn-accent/10 border border-cn-accent/20">
                <p className="text-[11px] uppercase tracking-wider text-cn-accent font-bold">Urgency level</p>
                <p className="font-display text-lg font-bold text-cn-text dark:text-white mt-1">{profile.urgency_level}</p>
              </div>
              <div className="p-4 rounded-2xl bg-cn-secondary/15 border border-cn-secondary/25">
                <p className="text-[11px] uppercase tracking-wider text-cn-secondary font-bold">Care type</p>
                <p className="font-display text-lg font-bold text-cn-text dark:text-white mt-1 capitalize">{profile.preferred_care_type?.replace('_', ' ')}</p>
              </div>
              <div className="md:col-span-3">
                <p className="text-[11px] uppercase tracking-wider text-cn-text-muted font-bold mb-2">Care needs</p>
                <div className="flex flex-wrap gap-2">
                  {profile.care_needs?.map((n, i) => (
                    <Badge key={i} variant="outline" className="px-3 py-1 border-cn-border">{n}</Badge>
                  ))}
                </div>
              </div>
            </div>
          ) : (
            <div className="mt-5 text-center py-10">
              <FileText className="w-10 h-10 text-cn-text-muted mx-auto mb-3" />
              <p className="text-sm text-cn-text-muted">Complete your assessment to see your personalized plan.</p>
            </div>
          )}
        </BentoTile>

        {/* Saved clinicians (shortlist) */}
        {savedClinicians.length > 0 && (
          <BentoTile span="col-span-full" className="p-6 sm:p-7" testId="saved-clinicians-section">
            <div className="flex items-center justify-between mb-4">
              <TileHeader eyebrow="Your shortlist" title="Saved clinicians" />
              <Link to="/patient/saved-clinicians" className="text-sm font-medium text-cn-primary inline-flex items-center gap-1 hover:gap-1.5 transition-all" data-testid="saved-browse-more">
                View all <ArrowRight className="w-4 h-4" />
              </Link>
            </div>
            <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-3">
              {savedClinicians.slice(0, 6).map((c) => (
                <div key={c.id} className="rounded-2xl border border-cn-border p-4 flex items-center gap-3 bg-cn-bg/40" data-testid={`saved-card-${c.id}`}>
                  {c.photo_url ? (
                    <img src={c.photo_url} alt={c.name} className="w-12 h-12 rounded-xl object-cover flex-shrink-0" />
                  ) : (
                    <div className="w-12 h-12 rounded-xl bg-cn-sage-100 flex items-center justify-center flex-shrink-0 text-cn-primary font-bold">
                      {(c.name || 'C').split(' ').map((n) => n[0]).join('').slice(0, 2)}
                    </div>
                  )}
                  <div className="min-w-0 flex-1">
                    <p className="font-semibold text-cn-text dark:text-white text-sm truncate">{c.name}</p>
                    <p className="text-xs text-cn-text-muted truncate">{c.license_type || c.headline || 'Clinician'}</p>
                    <div className="flex items-center gap-2 mt-1.5">
                      <Link to={`/patient/book/${c.id}`} className="text-xs font-semibold text-cn-primary hover:underline" data-testid={`saved-book-${c.id}`}>Book</Link>
                      <span className="text-cn-border">·</span>
                      <button onClick={() => removeSaved(c.id)} className="text-xs text-cn-text-muted hover:text-red-500" data-testid={`saved-remove-${c.id}`}>Remove</button>
                    </div>
                  </div>
                </div>
              ))}
            </div>
          </BentoTile>
        )}
      </BentoGrid>
    </DashboardShell>
  );
}
